import { z } from 'zod'
import { nullableArray, SlotSchema, toInterval, WarningSchema } from '@/shared/api'
import type { Interval } from '@/shared/lib'

/**
 * Причина закрытого времени (`schedule.BlockReason`) — закрытый список: по нему
 * сетка подписывает блок. Свободная формулировка живёт в `note` и обязательна
 * только для «своё».
 */
export const REASONS = ['day-off', 'vacation', 'sick', 'busy', 'other'] as const

export type BlockReason = (typeof REASONS)[number]

export const ReasonSchema = z.enum(REASONS)

/**
 * Занятое время (`schedule.TimeBlock`): интервал, в который человека не ставят на
 * работу. Ни названия, ни отметки выполнения, ни привязки к выходу — в список дел
 * оно не попадает никогда, только закрывает окно в сетке.
 */
export const TimeBlockSchema = z
  .object({
    id: z.uuid(),
    employee_id: z.uuid(),
    slot: SlotSchema,
    reason: ReasonSchema,
    note: z.string().optional().default(''),
  })
  .transform((dto) => ({
    id: dto.id,
    employeeId: dto.employee_id,
    slot: toInterval(dto.slot),
    reason: dto.reason,
    note: dto.note,
  }))

export interface TimeBlock {
  id: string
  employeeId: string
  slot: Interval
  reason: BlockReason
  note: string
}

export const TimeBlockResultSchema = z.object({
  time_block: TimeBlockSchema,
  warnings: nullableArray(WarningSchema),
})

export const TimeBlocksSchema = z
  .object({ time_blocks: nullableArray(TimeBlockSchema) })
  .transform((dto) => dto.time_blocks)

export interface TimeBlockDraft {
  /** Пусто — «мне»: бэкенд подставит актора сам. */
  employeeId: string | null
  slot: Interval
  reason: BlockReason
  note: string
}

export interface TimeBlockPatch {
  slot?: Interval
  reason?: BlockReason
  note?: string
}
